import {
  Component, OnInit, OnDestroy
} from '@angular/core';
import {
  ActivatedRoute, Router
} from '@angular/router';
import {
  Validators, FormGroup, FormControl, ValidatorFn, ValidationErrors
} from '@angular/forms';
import AccountService from 'app/core/auth/account.service';
import { SessionStorageService } from 'ngx-webstorage';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import ConfirmationDialogService from 'app/core/confirmation-dialog/confirmation-dialog.service';
import { FormatPhoneNumber } from 'app/shared/util/request-util';
import ProfileRxService from '../profile-rx/profile-rx.service';
import NotificationMessageRxService from './notification-message-rx.service';

@Component({
  selector: 'jhi-notification-message-rx',
  templateUrl: './notification-message-rx.component.html',
  styleUrls: ['notification-message-rx.scss']
})
export default class NotificationMessageRxComponent implements OnInit, OnDestroy {
  private NotificationMessageRxComponent$: Subject<void> = new Subject<void>();

  notificationForm: FormGroup;

  submitted = false;

  userId: any;

  username: any;

  patientId: any;

  showError = false;

  currentUser: any;

  rxPayload = {};

  phPayload = {};

  userPrimaryEmail;

  userPrimaryPhone;

  isUpdateProfile = false;

  shippingAddress;

  slideScreen;

  profile;

  showLoader = false;

  agent: any;

  optedForCallNotificationVal = false;

  optedForEmailNotificationVal = false;

  optedForSMSNotificationVal = false;

  declineMessage = 'By declining this consent you will not receive text or emails for refill reminders or coupon status updates';

  successMessage = 'Notification Preferences saved successfully.';

  errorMessage = 'Notification Preferences not saved, Please Try Again.';

  communicationMethod;

  communicationValue;

  constructor(
    protected activatedRoute: ActivatedRoute,
    private notificationService: NotificationMessageRxService,
    private accountService: AccountService,
    private profileService: ProfileRxService,
    private $sessionStorage: SessionStorageService,
    private router: Router,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit() {
    this.agent = navigator.appVersion;
    this.showLoader = true;
    this.accountService.slideScreen.pipe(takeUntil(this.NotificationMessageRxComponent$)).subscribe((data) => {
      this.slideScreen = data;
    });
    this.notificationForm = new FormGroup(
      {
        phContactType: new FormControl('dnd', [Validators.required]),
        userEmail: new FormControl('', [Validators.email]),
        userPhone: new FormControl('', [Validators.minLength(14)]),
      },
      { validators: this.validateForm() }
    );

    this.accountService.identity().then((account) => {
      this.currentUser = account;

      if (this.currentUser) {
        this.userId = this.currentUser.id;
        this.username = this.currentUser.username;
        this.patientId = this.currentUser.enterpriseRxPatientId;

        this.setUserNotificationPreferences();
        this.getProfile();
      }
    });

    this.accountService
      .identity()
      .then((account) => {
        this.showLoader = false;
        this.currentUser = account;
      });
  }

  getProfile() {
    this.profileService.getProfile(this.currentUser.enterpriseRxPatientId)
      .pipe(takeUntil(this.NotificationMessageRxComponent$))
      .subscribe((res:any) => {
        this.profile = res;
        this.shippingAddress = res?.patientAddressDTO;
        if (res?.primaryEmailAddress) {
          this.userPrimaryEmail = res?.primaryEmailAddress;
          this.notificationForm.get('userEmail').setValue(res?.primaryEmailAddress);
          this.notificationForm.get('userEmail').disable();
        }
        if (res?.primaryContactDetails) {
          this.userPrimaryPhone = res?.primaryContactDetails;
          this.notificationForm.get('userPhone').setValue(FormatPhoneNumber(res?.primaryContactDetails));
          this.notificationForm.get('userPhone').disable();
        }
      });
  }

  validateForm(): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      if (group.get('phContactType').value === 'email' && !group.get('userEmail').value) {
        group.get('userEmail').setErrors({ required: true });
      } else if (group.get('userEmail').errors && !group.get('userEmail').errors.email) {
        group.get('userEmail').setErrors(null);
      }

      if ((group.get('phContactType').value === 'sms' || group.get('phContactType').value === 'call') && !group.get('userPhone').value) {
        group.get('userPhone').setErrors({ required: true });
      } else {
        group.get('userPhone').setErrors(null);
      }
      return null;
    };
  }

  submitNotification() {
    this.submitted = true;
    const f = this.notificationForm.value;
    this.rxPayload = {};
    this.phPayload = {};

    if (!this.notificationForm.invalid) {
      this.isUpdateProfile = false;

      if (
        this.userPrimaryEmail !== this.notificationForm.value.userEmail ||
        this.notificationForm.value.userPhone !== FormatPhoneNumber(this.userPrimaryPhone)
      ) {
        this.isUpdateProfile = true;
      }

      this.optedForCallNotificationVal = false;
      this.optedForEmailNotificationVal = false;
      this.optedForSMSNotificationVal = false;

      if (f.phContactType === 'dnd') {
        // DND
        this.optedForCallNotificationVal = true;
        this.$sessionStorage.store('showConsentScreen', true);
        this.communicationMethod = 'PHONE CALL';
        this.communicationValue = this.userPrimaryPhone;
      }

      if (f.phContactType === 'email') {
        this.optedForEmailNotificationVal = true;
        this.$sessionStorage.store('showConsentScreen', false);
        this.communicationMethod = 'EMAIL';
        this.communicationValue = this.userPrimaryEmail;
      }

      if (f.phContactType === 'sms') {
        this.optedForSMSNotificationVal = true;
        this.$sessionStorage.store('showConsentScreen', false);
        this.communicationMethod = 'TEXT MESSAGE';
        this.communicationValue = this.userPrimaryPhone;
      }

      const jsonPayloadStr = {
        patientId: this.patientId,
        optedForEmailNotification: this.optedForEmailNotificationVal,
        optedForSMSNotification: this.optedForSMSNotificationVal,
        optedForCallNotification: this.optedForCallNotificationVal,
        communicationMethod: this.communicationMethod,
        communicationValue: this.communicationValue
      };

      const method = 'UPDATE';
      jsonPayloadStr && this.optedForCallNotificationVal ? this.doConfirm(jsonPayloadStr, method) : this.saveNotification(jsonPayloadStr, method);
    }
  }

  saveNotification(payload: any, method: string) {
    this.showLoader = true;
    this.notificationService.submitNotificationSettings(payload, method)
      .pipe(takeUntil(this.NotificationMessageRxComponent$))
      .subscribe({
        next: () => {
          payload.optedForCallNotification ? this.rejectNotificationConsent() : '';
          if (this.isUpdateProfile) {
            this.updateProfile();
          }
          this.showLoader = false;
          this.optedForCallNotificationVal ?
            this.openDialogueBox(this.successMessage, 'OK', 'Select a Notification Preference', false) :
            this.openDialogueBox(this.successMessage, 'OK', '', false);
        },
        error: () => {
          this.showLoader = false;
          this.openDialogueBox(this.errorMessage, '', 'TRY AGAIN', true);
        }
      });
  }

  rejectNotificationConsent() {
    const payload = {
      consent: 0,
      primaryUserPatientId: this.currentUser.enterpriseRxPatientId,
      userId: this.currentUser.id
    };
    this.notificationService.updatedNotificationConsent(payload)
      .subscribe({
        error: () => this.openDialogueBox(this.errorMessage, '', 'TRY AGAIN', true)
      });
  }

  updateProfile() {
    const payload = {
      edits: [],
      patientId: this.currentUser.enterpriseRxPatientId,
      admin: false,
      firstName: this.profile.firstName,
      lastName: this.profile.lastName
    };
    let submitUpdateForm = false;
    if (this.userPrimaryEmail !== this.notificationForm.value.userEmail && this.notificationForm.value.userEmail) {
      submitUpdateForm = true;
      if (!payload?.edits[0]) {
        payload.edits[0] = { emails: [] };
      }
      payload.edits[0].emails = [
        {
          address: this.notificationForm.value.userEmail,
          rank: '1',
          useForNotifier: true
        }
      ];
    }

    if (this.notificationForm.value.userPhone !== FormatPhoneNumber(this.userPrimaryPhone) && this.notificationForm.value.userPhone) {
      submitUpdateForm = true;
      if (!payload?.edits[0]) {
        payload.edits[0] = {
          phoneNumbers: [],
          shippingAddress: {}
        };
      }

      payload.edits[0].phoneNumbers = [
        {
          phonenumber: this.getValidPhoneNumber(this.notificationForm.value.userPhone),
          type: 'Cell',
          usage: 'Cell'
        }
      ];

      payload.edits[0].shippingAddress
        .payload.edits[0].shippingAddress = {
          address: this.shippingAddress.line1,
          city: this.shippingAddress.city,
          phoneNumbers: [
            {
              phonenumber: this.getValidPhoneNumber(this.notificationForm.value.userPhone),
              type: 'Primary',
              usage: 'Primary'
            }
          ],
          state: this.shippingAddress.state,
          zipCode: this.shippingAddress.zip
        };
    }
    if (submitUpdateForm) {
      this.profileService
        .update(payload)
        .pipe(takeUntil(this.NotificationMessageRxComponent$))
        .subscribe(() => {}, () => {});
    }
  }

  getValidPhoneNumber(phoneNumber) {
    phoneNumber = phoneNumber
      .replace('(', '')
      .replace(') ', '')
      .replace('-', '')
      .replace(' ', '');
    return `${phoneNumber.substr(0, 3)}-${phoneNumber.substr(3, phoneNumber.length - 1)}`;
  }

  setUserNotificationPreferences() {
    if (this.currentUser.optedForEmailNotification) {
      this.notificationForm.get('phContactType').setValue('email');
    } else if (this.currentUser.optedForSMSNotification) {
      this.notificationForm.get('phContactType').setValue('sms');
    } else if (this.currentUser.optedForCallNotification) {
      this.notificationForm.get('phContactType').setValue('dnd');
    } else {
      this.openDialogueBox('Please call Pharmacy to enroll in Notifications.', 'OK', '', true);
    }
  }

  doConfirm(payload: any, method: string) {
    this.showLoader = false;
    this.confirmationDialogService.confirm('Are you sure?', this.declineMessage, 'CONFIRM', 'CANCEL', 'lg').then((confirmed) => {
      confirmed ? this.saveNotification(payload, method) : '';
    });
  }

  openDialogueBox(message: string, confirmBtnTxt: string, cancelTxtBtn: string, isError: boolean) {
    this.confirmationDialogService.confirm('Notifications', message, confirmBtnTxt, cancelTxtBtn, 'lg', false, false, isError).then((confirmed) => {
      confirmed ? this.router.navigate(['/']) :
        this.optedForCallNotificationVal ?
          this.router.navigate(['/account/communication-consent/notification']) :
          this.router.navigate(['/notification-message-rx']);
    });
  }

  public ngOnDestroy(): void {
    this.NotificationMessageRxComponent$.next();
    this.NotificationMessageRxComponent$.unsubscribe();
  }
}
